/**
 * @fileoverview Shared hook for grid pages that render DataGrid directly (not GridPageShell).
 * @module ui-admin/GridPageShell/useGridPage
 *
 * Responsibilities:
 * - Theme CSS, grid-state autosave, template load, and panel-resize helpers.
 *
 * Dependencies:
 * - ThemeContext, FeatureTierContext, DataGrid GridState/GridTemplate types,
 *   gridAutosaveKey and persistAppliedGridState from DataGrid persistence helpers.
 *
 * Architecture:
 * - Presentation-only hook. Pages still own Dexie reads and pass row arrays
 *   into DataGrid; this hook only manages layout chrome and localStorage.
 *
 * Notes:
 * - Autosave key must stay in lockstep with gridPersistence.gridAutosaveKey.
 * - Mount restores leftover autosave unless a pending starred default is waiting.
 * - Apply writes autosave + colvis + colorder together so remount cannot revive
 *   the previous column layout from DataGrid's independent persistence layer.
 * - loadTemplates restores seeded system templates from code so a mutated
 *   stored "Default View" cannot keep another template's column map.
 */

import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useTheme, themeCSS } from '../../contexts/ThemeContext';
import type { GridTheme } from '../../contexts/ThemeContext';
import { useFeatureTier } from '../../contexts/FeatureTierContext';
import { persistAppliedGridState, cloneGridState, gridStatesEqual, readAppliedTemplate, writeAppliedTemplate, applyGridTemplateMeta, resolveMountGridState } from '../DataGrid';
import type { AppliedGridTemplateMeta } from '../DataGrid';
import type { GridState, GridTemplate } from '../DataGrid';

// ============================================================
// localStorage helpers
// ============================================================

function templatesKey(gridId: string) { return `datagrid_${gridId}_templates`; }

export function generateTemplateId() {
  return `tpl-${Date.now().toString(16).slice(-8)}${Math.random().toString(16).slice(2, 10)}`;
}

// ============================================================
// Template persistence
// ============================================================

export function loadTemplates(gridId: string, seeds: GridTemplate[]): GridTemplate[] {
  try {
    const stored = localStorage.getItem(templatesKey(gridId));
    if (stored) {
      const parsed = JSON.parse(stored) as GridTemplate[];
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]._id) {
        return mergeSeededSystemTemplates(parsed, seeds, gridId);
      }
    }
  } catch { /* ignore */ }
  const cloned = seeds.map((seed) => ({ ...seed, state: cloneGridState(seed.state) }));
  saveTemplates(gridId, cloned);
  return cloned;
}

/**
 * @process mergeSeededSystemTemplates
 * Steps: 1. Keep user personal/shared templates from localStorage.
 *        2. Restore every seed system template's state from code (never from
 *           a previously mutated stored snapshot).
 *        3. Persist the merged list when system snapshots drifted.
 * Inputs: stored templates, current seed templates, gridId.
 * Outputs: Merged GridTemplate[] with pristine system layouts.
 * Failure Handling: missing seeds → return stored list unchanged.
 * Idempotency: REQUIRED (re-running with the same seeds is a no-op after persist).
 */
function mergeSeededSystemTemplates(
  stored: GridTemplate[],
  seeds: GridTemplate[],
  gridId: string,
): GridTemplate[] {
  if (seeds.length === 0) return stored;
  const seedById = new Map(seeds.map((seed) => [seed._id, seed]));
  const storedById = new Map(stored.map((tpl) => [tpl._id, tpl]));
  const restoredSeeds = seeds.map((seed) => {
    const previous = storedById.get(seed._id);
    return {
      ...seed,
      state: cloneGridState(seed.state),
      isDefault: previous?.isDefault ?? seed.isDefault,
      isPinned: previous?.isPinned ?? seed.isPinned,
      usageCount: previous?.usageCount ?? seed.usageCount,
    };
  });
  const keptUser = stored.filter((tpl) => !seedById.has(tpl._id));
  const merged = [...restoredSeeds, ...keptUser];
  const drifted = restoredSeeds.some((seed) => {
    const previous = storedById.get(seed._id);
    return !previous || JSON.stringify(previous.state) !== JSON.stringify(seed.state);
  });
  if (drifted) {
    saveTemplates(gridId, merged);
  }
  return merged;
}

export function saveTemplates(gridId: string, templates: GridTemplate[]) {
  try { localStorage.setItem(templatesKey(gridId), JSON.stringify(templates)); } catch { /* ignore */ }
}

/**
 * @hook useAppliedTemplateDrift
 * Purpose:     Exact-state semantics for the applied-template label shared by
 *              GridPageShell and useGridPage. The label names the applied
 *              template only while the grid still shows that template's state.
 * Returns:    appliedTemplateName, trackStateDrift(state), markTemplateApplied(meta?)
 * Side Effects: reads/writes the grid_applied_template_* localStorage marker.
 * Notes:      The drift baseline is the first onStateChange emission after
 *             apply/mount — already DataGrid-normalized, so comparisons are
 *             emission-to-emission with no template-state normalization.
 */
export function useAppliedTemplateDrift(gridId: string) {
  const [appliedTemplateName, setAppliedTemplateName] = useState<string | null>(
    () => readAppliedTemplate(gridId)?.name ?? null,
  );
  const appliedTemplateBaseline = useRef<GridState | null>(null);

  const trackStateDrift = useCallback((state: GridState) => {
    // The first diverging edit clears the marker so the label never names a
    // stale layout.
    if (!appliedTemplateName) return;
    if (appliedTemplateBaseline.current === null) {
      appliedTemplateBaseline.current = cloneGridState(state);
    } else if (!gridStatesEqual(state, appliedTemplateBaseline.current)) {
      appliedTemplateBaseline.current = null;
      setAppliedTemplateName(null);
      writeAppliedTemplate(gridId, null);
    }
  }, [gridId, appliedTemplateName]);

  const markTemplateApplied = useCallback((meta: AppliedGridTemplateMeta) => {
    // Tri-state meta: {id,name} sets the badge, null clears it (factory
    // reset), undefined leaves it unchanged — handled by the shared helper.
    applyGridTemplateMeta(gridId, meta, setAppliedTemplateName);
    // Reset the drift baseline regardless — every apply remounts the grid and
    // its first emission reseeds the baseline with the applied layout as the
    // grid actually rendered it.
    appliedTemplateBaseline.current = null;
  }, [gridId]);

  return { appliedTemplateName, trackStateDrift, markTemplateApplied };
}

// ============================================================
// useGridPage Hook
// ============================================================

export interface UseGridPageOptions {
  gridId: string;
  defaultPanelWidth?: number;
  seedTemplates?: GridTemplate[];
}

export interface UseGridPageReturn {
  // Theme
  theme: GridTheme;
  themeCssString: string;
  // Grid state
  gridState: Partial<GridState>;
  gridStateKey: number;
  handleGridStateChange: (state: GridState) => void;
  handleApplyTemplate: (state: GridState, meta?: { id: string; name: string } | null) => void;
  handleRefresh: () => void;
  // Templates
  templateCount: number;
  appliedTemplateName: string | null;
  showTemplatesModal: boolean;
  setShowTemplatesModal: (v: boolean) => void;
  currentGridState: React.MutableRefObject<GridState>;
  // Panel
  panelWidth: number;
  setPanelWidth: React.Dispatch<React.SetStateAction<number>>;
  panelVisible: boolean;
  setPanelVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isResizing: React.MutableRefObject<boolean>;
  containerRef: React.RefObject<HTMLDivElement>;
  startResize: (e: React.MouseEvent) => void;
  // Feature tier
  isTier2: boolean;
  isAdmin: boolean;
  tier: number;
}

export default function useGridPage({
  gridId,
  defaultPanelWidth = 480,
  seedTemplates = [],
}: UseGridPageOptions): UseGridPageReturn {
  const { theme } = useTheme();
  const { isTier2, isAdmin, tier } = useFeatureTier();
  const containerRef = useRef<HTMLDivElement>(null!);

  // Theme CSS — memoized
  const themeCssString = useMemo(() => themeCSS(theme), [theme]);

  // Grid state persistence
  const [gridState, setGridState] = useState<Partial<GridState>>(
    () => resolveMountGridState({
      gridId,
      templates: loadTemplates(gridId, seedTemplates),
    }),
  );
  const [gridStateKey, setGridStateKey] = useState(0);
  const currentGridState = useRef<GridState>(gridState as GridState);

  const { appliedTemplateName, trackStateDrift, markTemplateApplied } = useAppliedTemplateDrift(gridId);

  const handleGridStateChange = useCallback((state: GridState) => {
    currentGridState.current = state;
    persistAppliedGridState(gridId, state);
    trackStateDrift(state);
  }, [gridId, trackStateDrift]);

  const handleApplyTemplate = useCallback((state: GridState, meta?: { id: string; name: string } | null) => {
    const snapshot = cloneGridState(state);
    persistAppliedGridState(gridId, snapshot);
    setGridState(snapshot);
    currentGridState.current = snapshot;
    setGridStateKey((k) => k + 1);
    markTemplateApplied(meta);
  }, [gridId, markTemplateApplied]);

  const handleRefresh = useCallback(() => {
    setGridState(currentGridState.current);
    setGridStateKey((k) => k + 1);
  }, []);

  // Templates
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [templateCount, setTemplateCount] = useState(() => loadTemplates(gridId, seedTemplates).length);
  // Re-derive the badge count when the modal closes — saves, deletes, and
  // updates inside the modal change the persisted template list.
  const templatesModalWasOpen = useRef(showTemplatesModal);
  useEffect(() => {
    if (templatesModalWasOpen.current && !showTemplatesModal) {
      setTemplateCount(loadTemplates(gridId, seedTemplates).length);
    }
    templatesModalWasOpen.current = showTemplatesModal;
  }, [showTemplatesModal, gridId, seedTemplates]);

  // Panel
  const [panelWidth, setPanelWidth] = useState(() => {
    try {
      const saved = localStorage.getItem(`${gridId}_panel_width`);
      if (saved) { const n = parseInt(saved, 10); if (n >= 280 && n <= 2000) return n; }
    } catch { /* ignore */ }
    return defaultPanelWidth;
  });
  const [panelVisible, setPanelVisible] = useState(false);
  const isResizing = useRef(false);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const startX = e.clientX;
    const startWidth = panelWidth;
    // Snapshot width ONCE at drag start — offsetWidth grows if panel overflows,
    // so reading it inside onMouseMove produces an ever-growing cap that never clamps.
    const fixedContainerWidth = containerRef.current?.offsetWidth || 1200;
    const onMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      const delta = startX - ev.clientX;
      setPanelWidth(Math.min(Math.max(280, startWidth + delta), fixedContainerWidth - 215));
    };
    const onMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setPanelWidth((w) => {
        try { localStorage.setItem(`${gridId}_panel_width`, String(w)); } catch { /* ignore */ }
        return w;
      });
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [panelWidth, gridId]);

  return {
    theme, themeCssString,
    gridState, gridStateKey, handleGridStateChange, handleApplyTemplate, handleRefresh,
    templateCount, appliedTemplateName, showTemplatesModal, setShowTemplatesModal, currentGridState,
    panelWidth, setPanelWidth, panelVisible, setPanelVisible, isResizing, containerRef, startResize,
    isTier2, isAdmin, tier,
  };
}
